import { useI18n } from 'vue-i18n'
import { useDateTime } from '@ai/ui-components'
import { useToast } from '@ai/ui-components/toast'
import type { ColdCallRow } from './useColdMonitoringCallDetails'
import type { ColdMonitoringCallDetailsResponse } from 'shared'
import {
  buildEventsExport,
  buildTranscriptExport,
  createLivekitContextEngine,
  hydrateContextSnapshot,
  type EventsExport,
  type TranscriptExport,
} from '@/composables/livekit'
import { timestampToUtcIsoString } from '@/utils/datetime'
import { downloadBlob } from '@/utils/download'
import { formatFileSize } from '@/utils/number'

type FetchCallDetails = (
  callId: string,
) => Promise<ColdMonitoringCallDetailsResponse | null | undefined>

type ColdCallJsonDownloadMode = 'raw-first' | 'ui-normalized-first'

interface UiNormalizedExport {
  call: ReturnType<typeof getCallMetadata>
  recording: ReturnType<typeof getRecording>
  summary: string | null
  transcript: TranscriptExport
  events: EventsExport
}

const getRecording = (audio: ColdMonitoringCallDetailsResponse['audio']) => ({
  url: audio.url,
  ...(audio.expiresAt !== undefined ? { expiresAt: audio.expiresAt } : {}),
  ...(audio.ttlSeconds !== undefined ? { ttlSeconds: audio.ttlSeconds } : {}),
})

const getCallMetadata = (call: ColdCallRow, details: ColdMonitoringCallDetailsResponse) => ({
  id: call.id,
  callerNumber: call.callerNumber,
  language: call.language,
  startTime: timestampToUtcIsoString(call.startTime),
  endTime: timestampToUtcIsoString(call.endTime),
  durationSeconds: call.durationSec,
  agent: call.agent,
  disconnectedBy: call.disconnectedBy,
  conversationOutput: details.conversationOutput ?? call.conversationOutput,
})

const getSummary = (call: ColdCallRow, details: ColdMonitoringCallDetailsResponse) =>
  details.summary ?? call.summary ?? null

const buildColdCallRawExport = (call: ColdCallRow, details: ColdMonitoringCallDetailsResponse) => ({
  call: {
    ...getCallMetadata(call, details),
    summary: getSummary(call, details),
  },
  recording: getRecording(details.audio),
  context: details.context,
})

const buildColdCallUiNormalizedExport = (
  call: ColdCallRow,
  details: ColdMonitoringCallDetailsResponse,
  timerFormatter: ReturnType<typeof useDateTime>,
): UiNormalizedExport => {
  const engine = createLivekitContextEngine(timerFormatter)

  try {
    hydrateContextSnapshot(engine, details.context)

    return {
      call: getCallMetadata(call, details),
      recording: getRecording(details.audio),
      summary: getSummary(call, details),
      transcript: buildTranscriptExport(engine),
      events: buildEventsExport(engine, timerFormatter),
    }
  } finally {
    engine.resetContext()
  }
}

// Pre-production requirement: keep both builders available so the downloadable JSON format
// can be switched quickly between raw-first and ui-normalized-first while the final shape is validated.
const buildFileName = () => {
  const isoMinute = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '')
  return `calls-extract-${isoMinute}.json`
}

const toBlob = (payload: unknown) =>
  new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })

const buildFallbackDetails = (call: ColdCallRow): ColdMonitoringCallDetailsResponse => ({
  summary: call.summary ?? null,
  conversationOutput: call.conversationOutput ?? null,
  transcript: [],
  events: [],
  audio: { url: call.audioUrl ?? '' },
  context: null,
})

export const useJsonDownload = (options: {
  mode?: ColdCallJsonDownloadMode
  getCallById: (callId: string) => ColdCallRow | undefined
  fetchCallDetails: FetchCallDetails
}) => {
  const { t } = useI18n()
  const timerFormatter = useDateTime()
  const toast = useToast()
  const mode = options.mode ?? 'ui-normalized-first' // Use `raw-first` for backend-like JSON export

  const downloadCallJson = async (callId: string) => {
    try {
      const call = options.getCallById(callId)
      if (!call) return

      const details = (await options.fetchCallDetails(callId)) ?? buildFallbackDetails(call)
      const payload =
        mode === 'raw-first'
          ? buildColdCallRawExport(call, details)
          : buildColdCallUiNormalizedExport(call, details, timerFormatter)

      const blob = toBlob(payload)
      const fileName = buildFileName()
      downloadBlob(blob, fileName)

      toast.push({
        type: 'notice',
        description: `${fileName} – ${formatFileSize(blob.size)} - Done`,
        autoDismiss: 4000,
      })
    } catch (error) {
      console.error('Failed to download cold call JSON', error)
      toast.error(t('please_try_again'), { title: t('error_occurred') })
    }
  }

  return {
    downloadCallJson,
  }
}
